/**
 * Quarterly Business Review (QBR) Template Form
 * QBR metrics, analysis, and insights
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const QBRForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    company_name: '', department: '', quarter: 'Q1', year: new Date().getFullYear().toString(),
    date: new Date().toISOString().split('T')[0], prepared_by: '', presented_to: '',
    executive_summary: '', quarter_highlights: [''], quarter_challenges: [''],
    key_metrics: [{ name: '', current_value: '', target_value: '', previous_value: '', trend: 'Up', status: 'On Track' }],
    okr_progress: [{ objective: '', progress: 0, status: 'On Track', notes: '' }],
    next_quarter_priorities: [''], risks: [{ description: '', likelihood: 'Medium', impact: 'Medium', mitigation: '' }],
    resource_needs: [''], budget_requests: [{ item: '', amount: '', justification: '' }],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    department: 'Engineering',
    quarter: 'Q1',
    year: '2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'VP Engineering',
    presented_to: 'Executive Team',
    executive_summary: 'Q1 was a strong quarter for Engineering with Platform V2 reaching 85% completion and team productivity up 15%. Key achievements include successful launch of AI features beta and infrastructure migration to Kubernetes. Challenges included talent acquisition in a competitive market.',
    quarter_highlights: [
      'Platform V2 development at 85% complete, on track for Q2 launch',
      'AI-powered search feature launched in beta with 90% positive feedback',
      'Kubernetes migration completed - 40% reduction in infrastructure costs',
      'Zero critical production incidents for 60+ days',
      'Team grew from 40 to 48 engineers'
    ],
    quarter_challenges: [
      'Hiring took longer than expected - 2 senior roles still open',
      'Technical debt in legacy modules slowed new feature development',
      'Third-party API integration delays impacted mobile app timeline'
    ],
    key_metrics: [
      { name: 'Deployment Frequency', current_value: '15/week', target_value: '12/week', previous_value: '10/week', trend: 'Up', status: 'On Track' },
      { name: 'Lead Time for Changes', current_value: '2 days', target_value: '3 days', previous_value: '4 days', trend: 'Up', status: 'On Track' },
      { name: 'Change Failure Rate', current_value: '3%', target_value: '5%', previous_value: '8%', trend: 'Up', status: 'On Track' },
      { name: 'Mean Time to Recovery', current_value: '30 min', target_value: '60 min', previous_value: '90 min', trend: 'Up', status: 'On Track' },
      { name: 'Sprint Velocity', current_value: '85 pts', target_value: '80 pts', previous_value: '75 pts', trend: 'Up', status: 'On Track' },
      { name: 'Bug Backlog', current_value: '45', target_value: '30', previous_value: '60', trend: 'Up', status: 'At Risk' }
    ],
    okr_progress: [
      { objective: 'Launch Platform V2 with AI features', progress: 85, status: 'On Track', notes: 'Beta complete, full launch Q2' },
      { objective: 'Achieve 99.9% platform uptime', progress: 100, status: 'On Track', notes: 'Exceeded - 99.95% achieved' },
      { objective: 'Reduce infrastructure costs by 30%', progress: 130, status: 'On Track', notes: 'Achieved 40% reduction' },
      { objective: 'Grow engineering team to 50', progress: 96, status: 'At Risk', notes: '48 hired, 2 positions open' }
    ],
    next_quarter_priorities: [
      'Complete and launch Platform V2',
      'Scale infrastructure for 10x user growth',
      'Reduce bug backlog by 50%',
      'Fill remaining 2 senior engineering positions',
      'Complete SOC 2 Type II certification'
    ],
    risks: [
      { description: 'Platform V2 launch delay could impact revenue targets', likelihood: 'Low', impact: 'High', mitigation: 'Weekly executive review, dedicated QA resources' },
      { description: 'Key engineer departures could impact velocity', likelihood: 'Medium', impact: 'Medium', mitigation: 'Retention bonuses, improved career paths' },
      { description: 'Third-party API deprecation in Q2', likelihood: 'High', impact: 'Medium', mitigation: 'Migration plan in progress' }
    ],
    resource_needs: [
      '2 additional Senior Engineers for Platform V2',
      'DevOps contractor for Kubernetes optimization',
      'Security consultant for SOC 2 preparation'
    ],
    budget_requests: [
      { item: 'AWS Reserved Instances (3-year)', amount: '$150,000', justification: '35% savings vs on-demand' },
      { item: 'Engineering Productivity Tools', amount: '$25,000', justification: 'GitHub Copilot, monitoring upgrades' },
      { item: 'Security Assessment & Pen Testing', amount: '$40,000', justification: 'Required for SOC 2' }
    ],
    rai_assignments: [
      { task: 'Complete Platform V2 launch plan', responsible: 'Product Manager', accountable: 'VP Engineering', informed: 'CEO, Sales', due_date: '2025-02-15', status: 'In Progress' }
    ],
    followups: [
      { item: 'Schedule monthly engineering all-hands', owner: 'VP Engineering', due_date: '2025-01-20', priority: 'Medium', status: 'Pending' },
      { item: 'Finalize Q2 hiring plan', owner: 'Engineering Manager', due_date: '2025-01-25', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Quarterly Business Review Draft";
      const payload = {
        name: draftName,
        template_type: "Quarterly Business Review",
        template_id: "qbr",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "q-b-r", "Q B R", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("q-b-r", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/qbr`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `QBR_${formData.department.replace(/\s+/g, '_') || 'Company'}_${formData.quarter}_${formData.year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Quarterly Business Review",
        formData,
        settings: activeSettings || {},
        templateType: "Quarterly Business Review",
        templateId: "qbr",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const trends = ['Up', 'Down', 'Stable'];
  const statuses = ['On Track', 'At Risk', 'Off Track'];
  const likelihoods = ['Low', 'Medium', 'High'];
  const impacts = ['Low', 'Medium', 'High'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Quarterly Business Review</h1><p className="text-sm text-gray-500">QBR metrics, analysis, and insights</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>QBR Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><Label>Company *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Department</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="Engineering" /></div>
              <div><Label>Quarter</Label>
                <Select value={formData.quarter} onValueChange={(v) => setFormData({...formData, quarter: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{quarters.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Year</Label><Input value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} placeholder="2025" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <div><Label>Executive Summary</Label><Textarea value={formData.executive_summary} onChange={(e) => setFormData({...formData, executive_summary: e.target.value})} placeholder="High-level summary of the quarter..." rows={4} /></div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-3 rounded-lg">
                <Label className="font-semibold text-green-800">Quarter Highlights ✓</Label>
                {formData.quarter_highlights.map((h, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={h} onChange={(e) => { const hl = [...formData.quarter_highlights]; hl[idx] = e.target.value; setFormData({...formData, quarter_highlights: hl}); }} placeholder="Achievement" className="text-sm" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, quarter_highlights: formData.quarter_highlights.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setFormData({...formData, quarter_highlights: [...formData.quarter_highlights, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>

              <div className="bg-red-50 p-3 rounded-lg">
                <Label className="font-semibold text-red-800">Quarter Challenges ✗</Label>
                {formData.quarter_challenges.map((c, idx) => (
                  <div key={idx} className="flex gap-2 mt-2">
                    <Input value={c} onChange={(e) => { const ch = [...formData.quarter_challenges]; ch[idx] = e.target.value; setFormData({...formData, quarter_challenges: ch}); }} placeholder="Challenge" className="text-sm" />
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, quarter_challenges: formData.quarter_challenges.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setFormData({...formData, quarter_challenges: [...formData.quarter_challenges, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Key Metrics</Label>
              <div className="space-y-2 mt-2">
                {formData.key_metrics.map((m, idx) => (
                  <div key={idx} className="grid grid-cols-2 md:grid-cols-7 gap-2 bg-gray-50 p-2 rounded">
                    <div><Label className="text-xs">Metric</Label><Input value={m.name} onChange={(e) => { const km = [...formData.key_metrics]; km[idx].name = e.target.value; setFormData({...formData, key_metrics: km}); }} placeholder="Metric" className="text-sm" /></div>
                    <div><Label className="text-xs">Current</Label><Input value={m.current_value} onChange={(e) => { const km = [...formData.key_metrics]; km[idx].current_value = e.target.value; setFormData({...formData, key_metrics: km}); }} className="text-sm" /></div>
                    <div><Label className="text-xs">Target</Label><Input value={m.target_value} onChange={(e) => { const km = [...formData.key_metrics]; km[idx].target_value = e.target.value; setFormData({...formData, key_metrics: km}); }} className="text-sm" /></div>
                    <div><Label className="text-xs">Previous</Label><Input value={m.previous_value} onChange={(e) => { const km = [...formData.key_metrics]; km[idx].previous_value = e.target.value; setFormData({...formData, key_metrics: km}); }} className="text-sm" /></div>
                    <div><Label className="text-xs">Trend</Label>
                      <Select value={m.trend} onValueChange={(v) => { const km = [...formData.key_metrics]; km[idx].trend = v; setFormData({...formData, key_metrics: km}); }}>
                        <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{trends.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Status</Label>
                      <Select value={m.status} onValueChange={(v) => { const km = [...formData.key_metrics]; km[idx].status = v; setFormData({...formData, key_metrics: km}); }}>
                        <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end"><Button variant="ghost" size="icon" onClick={() => setFormData({...formData, key_metrics: formData.key_metrics.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, key_metrics: [...formData.key_metrics, { name: '', current_value: '', target_value: '', previous_value: '', trend: 'Up', status: 'On Track' }]})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add Metric</Button>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Next Quarter Priorities</Label>
              {formData.next_quarter_priorities.map((p, idx) => (
                <div key={idx} className="flex gap-2 mt-2">
                  <Input value={p} onChange={(e) => { const np = [...formData.next_quarter_priorities]; np[idx] = e.target.value; setFormData({...formData, next_quarter_priorities: np}); }} placeholder="Priority" />
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, next_quarter_priorities: formData.next_quarter_priorities.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, next_quarter_priorities: [...formData.next_quarter_priorities, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default QBRForm;
